import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    booleanAttribute,
    numberAttribute
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatFormFieldAppearance, MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fromEvent, Subject } from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'nova-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatInputModule,
        MatAutocompleteModule,
        ReactiveFormsModule
    ]
})
export class NovaAutoCompleteComponent implements OnInit, OnChanges {
    @ViewChild('itemsListInput', { read: MatAutocompleteTrigger, static: true })
    private readonly _autocompleteTrigger: MatAutocompleteTrigger;

    @ViewChild('itemsListInput', { static: true })
    private readonly _itemsListInput: ElementRef;

    @Input()
    inputClass = '';

    @Input()
    inputAppearance: MatFormFieldAppearance = 'outline';

    @Input()
    items: unknown[];

    @Input()
    itemKey: string;

    @Input({ transform: booleanAttribute })
    hidePopupAfterSelection = false;

    @Input({ transform: booleanAttribute })
    hideArrow = true;

    @Input()
    placeholder = '';

    @Input()
    subscriptSizing: SubscriptSizing = 'fixed';

    @Input()
    displayFn: (item: unknown) => string = (item) => item?.[this.itemKey] ?? this.value;

    @Input()
    displayItemFn: (item: unknown) => string;

    @Input({ transform: numberAttribute })
    textFilterLength = 2;

    @Input({ transform: numberAttribute })
    delayLength = 250;

    @Input()
    value = '';

    @Input()
    icon = '';

    @Input()
    loading: boolean;

    @Input()
    dataQaId: string;

    @Input()
    disable$ = new Subject<boolean>();

    @Output()
    itemSelected = new EventEmitter();

    @Output()
    textUpdated = new EventEmitter<string>();

    @Output()
    clearedText = new EventEmitter<boolean>();

    stateControl = new FormControl();

    ngOnInit(): void {
        fromEvent(this._itemsListInput.nativeElement, 'keyup')
            .pipe(
                delay(this.delayLength),
                map(() => this._itemsListInput.nativeElement.value),
                filter((value: string) => value.length >= this.textFilterLength),
                untilDestroyed(this)
            )
            .subscribe((value) => this.textUpdated.emit(value));

        this.disable$.pipe(untilDestroyed(this)).subscribe((disable) => {
            if (disable) {
                this.stateControl.disable();
            } else {
                this.stateControl.enable();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes['items'] && changes['items'].currentValue) {
            this.items = changes['items'].currentValue;
        }

        if (changes && changes['value'] && changes['value'].currentValue) {
            this.value = changes['value'].currentValue;
        }
    }

    selectedItem(event: MatAutocompleteSelectedEvent): void {
        this.itemSelected.emit(event);

        if (this.hidePopupAfterSelection) {
            this._autocompleteTrigger.closePanel();
        }
    }

    displayItem(item) {
        return typeof this.displayItemFn === 'function' ? this.displayItemFn(item) : item[this.itemKey];
    }

    clearText(event: FocusEvent): void {
        if (!(event.target as any).value) {
            this.clearedText.emit(true);
        }
    }
}
